/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import { encodeBase64, VSBuffer } from '../../../base/common/buffer.js';

export type ShellInitScriptShell = 'bash' | 'powershell';

/**
 * One client-generated script sourced before every SDK built-in shell command.
 * The array-valued session config carries either no script (`[]`) or this one
 * script, so clearing a previously applied script is explicit.
 */
export interface IShellInitScript {
	readonly shell: ShellInitScriptShell;
	readonly script: string;
}

function quoteBash(value: string): string {
	return `'${value.replaceAll(`'`, `'\\''`)}'`;
}

function encodedPowerShellExpression(value: string): string {
	const encoded = encodeBase64(VSBuffer.fromString(value));
	return `[System.Text.Encoding]::UTF8.GetString([System.Convert]::FromBase64String('${encoded}'))`;
}

function powerShellBlock(body: readonly string[], failureMessage: string): string[] {
	return [
		`$__vscodePreviousErrorActionPreference = $ErrorActionPreference`,
		`try {`,
		`\t$ErrorActionPreference = 'Stop'`,
		...body,
		`} catch {`,
		`\tWrite-Output '${failureMessage.replaceAll(`'`, `''`)}'`,
		`} finally {`,
		`\t$ErrorActionPreference = $__vscodePreviousErrorActionPreference`,
		`}`,
	];
}

/**
 * Creates the single script VS Code registers with the SDK shell tool.
 *
 * Profile loading comes first so activation runs against the user's shell
 * setup. Activation is whatever command the Python Environments extension
 * published for the folder; nothing tool-specific is added here.
 *
 * Every script ends successfully unless sourced profile code itself
 * terminates the shell: the runtime reports a nonzero init-script status
 * before every later command, and discards Bash init-script stderr.
 */
export function createShellInitScript(shell: ShellInitScriptShell, pythonActivation: string | undefined): IShellInitScript {
	return shell === 'powershell'
		? createPowerShellInitScript(pythonActivation)
		: createBashInitScript(pythonActivation);
}

function createBashInitScript(pythonActivation: string | undefined): IShellInitScript {
	const lines = [
		`# Generated by VS Code for Agent Host shell commands.`,
		`if [ -r "$HOME/.bashrc" ]; then`,
		// An rc file's status is the status of its final command. A nonzero
		// status therefore does not mean profile setup failed.
		`\tbuiltin source "$HOME/.bashrc" || builtin true`,
		`fi`,
	];
	if (pythonActivation?.trim()) {
		lines.push(
			`if ! builtin eval ${quoteBash(pythonActivation)}; then`,
			`\tprintf '%s\\n' 'copilot shell init: Python activation failed; continuing without the selected environment.'`,
			`fi`,
		);
	}
	lines.push(`builtin true`, ``);
	return { shell: 'bash', script: lines.join('\n') };
}

function createPowerShellInitScript(pythonActivation: string | undefined): IShellInitScript {
	// Profiles load with their normal 'Continue' preference — the runtime
	// sources init scripts under 'Stop', which would let one benign
	// non-terminating profile error skip everything after it. Each profile has
	// its own try/catch so a broken profile does not skip the next one. A
	// preference changed by a profile affects later init work; the runtime may
	// restore its outer preference after this script completes.
	const lines = [
		`# Generated by VS Code for Agent Host shell commands.`,
		`$ErrorActionPreference = 'Continue'`,
		`foreach ($__vscodeProfile in @($PROFILE.CurrentUserAllHosts, $PROFILE.CurrentUserCurrentHost)) {`,
		`\ttry {`,
		`\t\tif ($__vscodeProfile -and (Test-Path -LiteralPath $__vscodeProfile)) {`,
		`\t\t\t. $__vscodeProfile`,
		`\t\t}`,
		`\t} catch {`,
		`\t\tWrite-Output 'copilot shell init: loading the PowerShell profile failed; continuing.'`,
		`\t}`,
		`}`,
	];
	if (pythonActivation?.trim()) {
		lines.push(...powerShellBlock(
			[`\tInvoke-Expression (${encodedPowerShellExpression(pythonActivation)})`],
			'copilot shell init: Python activation failed; continuing without the selected environment.',
		));
	}
	lines.push(`$global:LASTEXITCODE = 0`, ``);
	return { shell: 'powershell', script: lines.join('\n') };
}

/** Generated scripts are a few hundred bytes; anything near this is not one. */
const MAX_SHELL_INIT_SCRIPT_LENGTH = 64 * 1024;

/** Validates the client-pushed list before the agent host writes it to disk. */
export function isShellInitScriptList(value: unknown): value is readonly IShellInitScript[] {
	return Array.isArray(value)
		&& value.length <= 1
		&& value.every(entry => {
			if (!entry || typeof entry !== 'object') {
				return false;
			}
			const script = entry as Partial<IShellInitScript>;
			return (script.shell === 'bash' || script.shell === 'powershell')
				&& typeof script.script === 'string'
				&& script.script.length > 0
				&& script.script.length <= MAX_SHELL_INIT_SCRIPT_LENGTH;
		});
}
